import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogClose,
    DialogDescription,
} from '@/components/ui/dialog';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import EDatePicker from '@/components/everridge/EDatePicker';
import { FinancesPageFilterFormData, FinancesPageFilterFormSchema } from '@/features/finances/validators/FinancesPageFilterValidator';
import { useFinancesPageFilter } from '@/features/finances/store/useFinancesPageFilter';
import ApartmentsLookup from '@/features/apartments/components/ApartmentsLookup';
import { cn } from '@/lib/utils';

type Props = {
    onClose: () => void;
    isMobile?: boolean;
    showApartmentsLookup?: boolean;
};

const FinancePageFilter: FC<Props> = ({ onClose, isMobile, showApartmentsLookup = true }) => {
    const [isOpen, setIsOpen] = useState(true);

    const { t } = useTranslation();
    const { filter, setFilter } = useFinancesPageFilter();

    const { control, handleSubmit, reset } = useForm<FinancesPageFilterFormData>({
        resolver: zodResolver(FinancesPageFilterFormSchema),
        values: {
            startDate: filter.startDate,
            endDate: filter.endDate,
            apartmentId: filter.apartmentId,
        },
    });

    const handleClose = () => {
        setIsOpen(false);
        setTimeout(onClose, 300);
    };

    const handleSubmitFilterForm = (form: FinancesPageFilterFormData) => {
        setFilter(form);
        handleClose();
    };

    const handleResetFilter = () => {
        reset({
            startDate: null,
            endDate: null,
            apartmentId: [],
        });
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogDescription />
                    <DialogTitle>
                        {t('elements.components.filter.title')}
                        <DialogClose
                            className={cn(
                                'ml-auto flex items-center justify-center cursor-pointer',
                                isMobile && 'size-11 border border-popover-100 rounded-full',
                            )}
                        >
                            <Cancel_01_Icon />
                        </DialogClose>
                    </DialogTitle>
                </DialogHeader>
                <div className="flex flex-col gap-4">
                    <div className="flex gap-4">
                        <EDatePicker
                            control={control}
                            fieldName={'startDate'}
                            label={t('elements.form.startDate.label')}
                            placeholder={t('elements.form.startDate.placeholder')}
                        />
                        <EDatePicker
                            control={control}
                            fieldName={'endDate'}
                            label={t('elements.form.endDate.label')}
                            placeholder={t('elements.form.endDate.placeholder')}
                        />
                    </div>
                    {showApartmentsLookup && (
                        <ApartmentsLookup
                            control={control}
                            fieldName={'apartmentId'}
                            selectVariant="multi"
                            label={t('elements.form.apartments.label')}
                            placeholder={t('elements.form.apartments.placeholder')}
                        />
                    )}
                </div>
                <DialogFooter className={cn('justify-between', isMobile && 'justify-end mt-auto')}>
                    <Button variant="outline" onClick={handleResetFilter}>
                        {t('elements.buttons.reset')}
                    </Button>
                    <Button onClick={handleSubmit(handleSubmitFilterForm)}>
                        {t('elements.buttons.apply')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default FinancePageFilter;
